import { useCallback, useEffect, useMemo, useState } from 'react';
import { listSubmissions, claimNextSubmission, ApiError } from '../lib/api';
import type {
  ArchiveRecord,
  ListSubmissionsRequest,
  ClaimNextNotBalancedError,
} from '../types/moderation';
import { setSubmissionNavCache } from '../lib/submission-nav';
import { getThumbnailUrl } from '../lib/media';
import { formatDate, relativeTime } from '../lib/format';
import StatusBadge from './StatusBadge';
import Skeleton from './ui/Skeleton';
import EmptyState from './ui/EmptyState';
import ErrorState from './ErrorState';
import {
  PULL_TO_REFRESH_THRESHOLD,
  usePullToRefresh,
} from '../lib/use-pull-to-refresh';

interface MobileQueueProps {
  onSelect: (submissionId: string) => void;
  initialFilters?: ListSubmissionsRequest;
}

const PAGE_SIZE = 20;

const QUICK_FILTERS: Array<{ label: string; status?: string }> = [
  { label: 'Pending', status: 'pending_review' },
  { label: 'Awaiting peer', status: 'awaiting_peer_review' },
  { label: 'Approved', status: 'approved' },
  { label: 'All', status: undefined },
];

/**
 * Mobile-first queue feed.
 *
 * Renders a thumbnail-led list of submissions optimised for thumb scroll:
 * - Status chip row at the top swaps between common moderation states
 *   without needing to open a separate filter sheet.
 * - Each card is a single tap target with a 96 px thumbnail, the title,
 *   the moderation status pill, AI confidence, and a relative time stamp.
 * - Pull-to-refresh reloads the current filter set; the refresh affordance
 *   sits inside the scroll region so iOS Safari does not also bounce the
 *   page.
 * - A floating "Claim next" CTA is pinned above the bottom tab bar so the
 *   moderator never has to scroll to find their next assignment.
 *
 * Filters other than status are deferred to a follow-up MobileFilterSheet;
 * this PR ships the most common triage path (status + tap a card).
 */
export default function MobileQueue({ onSelect, initialFilters }: MobileQueueProps) {
  const [status, setStatus] = useState<string | undefined>(
    initialFilters?.status || 'pending_review',
  );
  const [items, setItems] = useState<ArchiveRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claimBusy, setClaimBusy] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [notBalanced, setNotBalanced] = useState<ClaimNextNotBalancedError | null>(
    null,
  );
  const [reloadKey, setReloadKey] = useState(0);

  const filters = useMemo<ListSubmissionsRequest>(
    () => ({
      status,
      sort: 'createdAt',
      dir: 'desc',
      page: 1,
      pageSize: PAGE_SIZE,
    }),
    [status],
  );

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listSubmissions(filters);
      setItems(res.items);
      setTotal(res.total);
      setSubmissionNavCache(
        res.items.map((r) => r.submissionId),
        JSON.stringify(filters),
        res.items.map((r) => ({ id: r.submissionId, title: r.title || r.submissionId })),
      );
    } catch (err) {
      setError((err as Error).message || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    reload();
  }, [reload, reloadKey]);

  const { ref: scrollRef, pulling, distance, refreshing } = usePullToRefresh({
    onRefresh: reload,
    enabled: !loading,
  });

  const handleClaimNext = async (force = false) => {
    setClaimBusy(true);
    setClaimError(null);
    if (!force) setNotBalanced(null);
    try {
      const res = await claimNextSubmission(force);
      setNotBalanced(null);
      onSelect(res.record.submissionId);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setClaimError("No claimable submissions right now. Nice work.");
      } else if (err instanceof ApiError && err.status === 503) {
        setClaimError(
          'Auto-assignment is turned off. Pick a submission below or ask an admin to enable it.',
        );
      } else if (
        err instanceof ApiError &&
        err.status === 409 &&
        typeof err.body === 'object' &&
        err.body !== null &&
        (err.body as { code?: string }).code === 'NOT_BALANCED'
      ) {
        setNotBalanced(err.body as ClaimNextNotBalancedError);
      } else {
        setClaimError((err as Error).message || 'Could not claim a submission.');
      }
    } finally {
      setClaimBusy(false);
    }
  };

  // Distance translates the inner list down so the spinner is visible
  // above the first card. We clamp to the threshold + a touch of overshoot.
  const pullStyle = pulling
    ? { transform: `translateY(${distance}px)`, transition: 'none' as const }
    : refreshing
    ? {
        transform: `translateY(${PULL_TO_REFRESH_THRESHOLD - 16}px)`,
        transition: 'transform 200ms ease' as const,
      }
    : undefined;

  return (
    <div className="mobile-queue" data-testid="mobile-queue">
      <header className="mobile-queue__header">
        <h1 className="mobile-queue__title">Queue</h1>
        <p className="mobile-queue__summary" data-testid="queue-summary">
          {loading
            ? 'Loading…'
            : `${total.toLocaleString()} ${
                status ? status.replace(/_/g, ' ') : 'submissions'
              }`}
        </p>
      </header>
      <nav
        className="mobile-queue__chips"
        aria-label="Quick filters"
        data-testid="quick-filters"
      >
        {QUICK_FILTERS.map((f) => {
          const active = (f.status || '') === (status || '');
          return (
            <button
              key={f.label}
              type="button"
              className={`queue-chip${active ? ' queue-chip--active' : ''}`}
              aria-pressed={active}
              data-testid={`chip-${f.label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => setStatus(f.status)}
            >
              {f.label}
            </button>
          );
        })}
      </nav>

      <div
        className="mobile-queue__scroll"
        ref={scrollRef}
        data-testid="queue-scroll"
        aria-busy={loading || refreshing}
      >
        {(pulling || refreshing) && (
          <div
            className="mobile-queue__refresh-indicator"
            aria-hidden={!refreshing}
            data-testid="queue-refresh-indicator"
          >
            {refreshing
              ? 'Refreshing…'
              : distance >= PULL_TO_REFRESH_THRESHOLD
              ? 'Release to refresh'
              : 'Pull to refresh'}
          </div>
        )}
        <ul className="mobile-queue__list" style={pullStyle}>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <li key={i} className="queue-card queue-card--loading">
                <Skeleton variant="block" />
              </li>
            ))
          ) : error ? (
            <li>
              <ErrorState
                message="We could not load the queue."
                detail={error}
                onRetry={() => setReloadKey((k) => k + 1)}
                testId="queue-error"
              />
            </li>
          ) : items.length === 0 ? (
            <li>
              <EmptyState
                title="Nothing here right now"
                description="Change the filter above or pull to refresh."
                testId="queue-empty"
              />
            </li>
          ) : (
            items.map((item) => {
              const ai = item.aiAnalysis?.length
                ? item.aiAnalysis[item.aiAnalysis.length - 1]
                : null;
              const firstFile = item.files?.[0];
              const thumbUrl = firstFile ? getThumbnailUrl(firstFile, 'sm') : '';
              return (
                <li key={item.submissionId} className="queue-card">
                  <button
                    type="button"
                    className="queue-card__button"
                    onClick={() => onSelect(item.submissionId)}
                    data-testid={`queue-card-${item.submissionId}`}
                    aria-label={`Open ${item.title || 'submission'}`}
                  >
                    <span className="queue-card__thumb" aria-hidden="true">
                      {thumbUrl ? (
                        <img src={thumbUrl} alt="" loading="lazy" />
                      ) : (
                        <span className="queue-card__thumb-placeholder">
                          {item.mediaType ? item.mediaType[0] : '?'}
                        </span>
                      )}
                      {item.sensitive && (
                        <span
                          className="queue-card__sensitive"
                          aria-label="Sensitive"
                          title="Sensitive"
                        >
                          !
                        </span>
                      )}
                    </span>
                    <span className="queue-card__body">
                      <span className="queue-card__title">
                        {item.title || 'Untitled submission'}
                      </span>
                      <span className="queue-card__meta">
                        <StatusBadge status={item.moderationStatus} />
                        {item.mediaType && (
                          <span className="queue-card__type">{item.mediaType}</span>
                        )}
                        {item.decades && item.decades[0] && (
                          <span className="queue-card__type">
                            {item.decades[0]}
                          </span>
                        )}
                      </span>
                      <span className="queue-card__sub">
                        <span>
                          {item.contributor?.name || 'Anonymous'}
                          {' · '}
                          {relativeTime(item.createdAt) || formatDate(item.createdAt)}
                        </span>
                        {ai && (
                          <span
                            className={`queue-card__ai queue-card__ai--${
                              ai.confidence >= 0.85
                                ? 'high'
                                : ai.confidence >= 0.6
                                ? 'med'
                                : 'low'
                            }`}
                            data-testid={`queue-ai-${item.submissionId}`}
                          >
                            AI {Math.round(ai.confidence * 100)}%
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="queue-card__chevron" aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 16 16">
                        <path
                          d="M6 3l5 5-5 5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>

      <div className="mobile-queue__cta" data-testid="mobile-claim-next-wrap">
        {notBalanced && (
          <div
            className="mobile-queue__balance"
            role="status"
            data-testid="mobile-claim-next-not-balanced"
          >
            {notBalanced.mostUnderloaded} has fewer open claims (
            {notBalanced.lowestClaimCount}) than you (
            {notBalanced.yourClaimCount}). Give them a chance.
          </div>
        )}
        {claimError && (
          <div
            className="mobile-queue__claim-error"
            role="alert"
            data-testid="mobile-claim-next-error"
          >
            {claimError}
          </div>
        )}
        <button
          type="button"
          className="btn btn-primary mobile-queue__claim-btn"
          onClick={() => handleClaimNext(false)}
          disabled={claimBusy}
          data-testid="mobile-claim-next"
        >
          {claimBusy ? 'Claiming…' : 'Claim next'}
        </button>
      </div>
    </div>
  );
}
